import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useMatch } from '../context/MatchContext';
import ATC_TECHNIQUES from '../utils/atcTechniques';
import { calculatePoints } from '../utils/scoring';
import { buildKeyMap } from '../utils/keybindings';

// ── Dati ──────────────────────────────────────────────────────────────────────

const ACTION_TYPES = [
  { id: 'a',  label: 'Attacco',         abbr: 'A',  key: 'a' },
  { id: 'd',  label: 'Difesa',          abbr: 'D',  key: 'd' },
  { id: 'c',  label: 'Contrattacco',    abbr: 'C'  },
  { id: 'ca', label: 'Contro anticipo', abbr: 'Ca' },
  { id: 'ci', label: 'Contro incastro', abbr: 'Ci' },
  { id: 'c2', label: 'C. 2° tempo',     abbr: 'C2' },
];

const PENALTY_TYPES = [
  'Attacco dopo kallyo',
  'Uscita bordi',
  'Caduta',
  'Evitare combattimento',
  'Colpire basso',
  'Afferrare/spingere',
  'Altro',
];

// ── Componente principale ─────────────────────────────────────────────────────

function ActionComposer({ onAthleteSelect, onConfirm, onCancel, onAddPenalty, onDeletePenalty, kbMode = 'set1', set2Bindings, isEditing = false, onMarkStart, onMarkEnd }) {
  const { state, selectAthlete, setActionField } = useMatch();
  const { currentAction, session, penalties, currentRound } = state;

  const composerRef = useRef(null);

  const [showBiomech, setShowBiomech] = useState(false);
  const [customName,  setCustomName]  = useState('');
  const [penaltyType, setPenaltyType] = useState(PENALTY_TYPES[0]);
  const [seqActive,   setSeqActive]   = useState(false);

  const seqIdRef     = useRef(null);
  const seqActiveRef = useRef(false);
  const kbModeRef    = useRef('set1');
  const set2MapRef   = useRef(buildKeyMap(set2Bindings || {}));

  // Aggiorna mappa inversa quando cambiano i binding o la modalità
  useEffect(() => { set2MapRef.current = buildKeyMap(set2Bindings || {}); }, [set2Bindings]);

  // Forza il focus sul container al mount così le shortcut tastiera funzionano
  // subito senza dover prima cliccare qualcosa con il mouse
  useEffect(() => { composerRef.current?.focus(); }, []);

  // Ref aggiornati ad ogni render — permettono al keyboard handler (registrato
  // una sola volta) di leggere sempre lo stato e le callback più recenti
  const currentActionRef  = useRef(currentAction);
  currentActionRef.current = currentAction;

  const handleConfirmRef       = useRef(null);
  const onCancelRef            = useRef(onCancel);
  onCancelRef.current          = onCancel;
  const handleSelectAthleteRef = useRef(null);

  // Traccia quale shortcut numerico (1/2/3/4/6) è attualmente attivo
  // (gruppo radio: uno solo alla volta)
  const activeShortcutRef = useRef(null);

  // Detector sequenze a 2 tasti (es. x→s = sinistra, x→d = destra)
  const prevKeyRef = useRef({ key: '', time: 0 });

  const blueName = session?.athlete_blue_name || 'BLU';
  const redName  = session?.athlete_red_name  || 'ROSSO';

  const bluePens = penalties.filter(p => p.athlete === 'blue' && p.round_number === currentRound);
  const redPens  = penalties.filter(p => p.athlete === 'red'  && p.round_number === currentRound);

  // ── Selezione atleta (toggle) ─────────────────────────────────────────────
  const handleSelectAthlete = useCallback((athlete) => {
    if (currentActionRef.current.athlete === athlete) {
      setActionField('athlete', null);
    } else {
      selectAthlete(athlete);
      onAthleteSelect(athlete);
    }
  }, [selectAthlete, onAthleteSelect, setActionField]);
  handleSelectAthleteRef.current = handleSelectAthlete;

  // ── Selezione tecnica (toggle) ────────────────────────────────────────────
  const handleTechniqueSelect = useCallback((techId) => {
    const current = currentActionRef.current.technique;
    const next = current === techId ? null : techId;
    setActionField('technique', next);
    if (next !== 'custom') setActionField('customTechnique', '');
  }, [setActionField]);

  // ── Calcolo validità ─────────────────────────────────────────────────────
  const isClash = currentAction.technique === 'Clsh';

  const pts = calculatePoints(
    currentAction.technique,
    currentAction.target,
    currentAction.outcome,
    currentAction.technicalBonus
  );

  // Clash: nessun esito richiesto (è una difesa, non assegna punti)
  // Altri:  esito obbligatorio; se punto → bersaglio obbligatorio
  const outcomeOk = isClash
    ? true
    : !!(currentAction.outcome) && (
        currentAction.outcome === 'tocco' ||
        currentAction.outcome === 'miss'  ||
        (currentAction.outcome === 'punto' && !!currentAction.target)
      );

  const isValid = !!(
    currentAction.athlete &&
    currentAction.actionType &&
    currentAction.technique &&
    outcomeOk
  );

  // ── Toggle sequenza ──────────────────────────────────────────────────────
  const toggleSeq = useCallback(() => {
    const next = !seqActiveRef.current;
    seqActiveRef.current = next;
    setSeqActive(next);
    if (next) {
      // Nuovo ID di sequenza quando si attiva
      seqIdRef.current = Date.now().toString(36) + Math.random().toString(36).slice(2, 5);
    }
  }, []);

  // ── Conferma ─────────────────────────────────────────────────────────────
  const handleConfirm = useCallback(() => {
    if (!isValid) return;
    onConfirm({
      ...currentAction,
      points: pts,
      sequence_id: seqActiveRef.current ? seqIdRef.current : null,
    });
    setCustomName('');
    activeShortcutRef.current = null;   // reset gruppo radio dopo conferma
  }, [isValid, onConfirm, currentAction, pts]);

  // Aggiorna i ref ogni render così il keyboard handler usa sempre la versione fresca
  handleConfirmRef.current = handleConfirm;
  kbModeRef.current = kbMode;
  const toggleSeqRef = useRef(toggleSeq);
  toggleSeqRef.current = toggleSeq;

  // ── Helpers shortcut radio ────────────────────────────────────────────────
  // Ogni shortcut 1-6 si comporta come un radio button:
  //   - premerne uno diverso → spegne il precedente, accende il nuovo
  //   - premere lo stesso due volte → spegne (toggle off)

  // Azzera i campi "di proprietà" dello shortcut attivo
  const clearShortcut = useCallback((key) => {
    // Tutti gli shortcut possono impostare target, outcome, technicalBonus
    setActionField('target',         null);
    setActionField('outcome',        null);
    setActionField('technicalBonus', false);
    // Solo lo shortcut 1 imposta anche tecnica e tipo azione
    if (key === '1') {
      setActionField('technique',  null);
      setActionField('actionType', null);
    }
  }, [setActionField]);

  // ── Keyboard shortcuts ────────────────────────────────────────────────────
  // B/R     → atleta blu/rosso (toggle)
  // A       → tipo: attacco (toggle)
  // D       → tipo: difesa  (toggle)   [non attiva se preceduto da X → xd = destra]
  // 1       → Pugno + corpo + punto + attacco  ┐
  // 2       → corpo + punto                    │
  // 3       → testa + punto                    ├ gruppo radio (uno alla volta)
  // 4       → corpo + rotazione + punto        │
  // 6       → testa + rotazione + punto        ┘
  // x→s    → gamba sinistra (toggle)
  // x→d    → gamba destra   (toggle)   [ha priorità su D singolo]
  // g→c    → guardia chiusa (toggle)
  // g→a    → guardia aperta (toggle)   [ha priorità su A singolo]
  // c→l    → distanza clinch (toggle)
  // +       → toggle modalità sequenza (sticky: tutte le azioni successive condividono sequence_id)
  // Enter   → conferma · Escape → annulla
  useEffect(() => {
    const handler = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      // ── Set 2: tasto singolo da mappatura configurabile ─────────────────
      if (kbModeRef.current === 'set2') {
        const actionId = set2MapRef.current[e.code];
        if (actionId && !actionId.startsWith('video_')) {
          e.preventDefault();
          const ca = currentActionRef.current;
          switch (actionId) {
            case 'athlete_blue':      handleSelectAthleteRef.current?.('blue'); break;
            case 'athlete_red':       handleSelectAthleteRef.current?.('red');  break;
            case 'type_attack':       setActionField('actionType', ca.actionType === 'a'  ? null : 'a');  break;
            case 'type_defense':      setActionField('actionType', ca.actionType === 'd'  ? null : 'd');  break;
            case 'type_counter':      setActionField('actionType', ca.actionType === 'c'  ? null : 'c');  break;
            case 'type_counter_ant':  setActionField('actionType', ca.actionType === 'ca' ? null : 'ca'); break;
            case 'type_counter_int':  setActionField('actionType', ca.actionType === 'ci' ? null : 'ci'); break;
            case 'type_counter_2nd':  setActionField('actionType', ca.actionType === 'c2' ? null : 'c2'); break;
            case 'outcome_point':     setActionField('outcome', ca.outcome === 'punto' ? null : 'punto'); break;
            case 'outcome_touch':     setActionField('outcome', ca.outcome === 'tocco' ? null : 'tocco'); break;
            case 'outcome_miss':      setActionField('outcome', ca.outcome === 'miss'  ? null : 'miss');  break;
            case 'target_body':       setActionField('target', ca.target === 'corpo' ? null : 'corpo');   break;
            case 'target_head':       setActionField('target', ca.target === 'testa' ? null : 'testa');   break;
            case 'leg_left':          setActionField('legSide', ca.legSide === 'sinistra' ? null : 'sinistra'); break;
            case 'leg_right':         setActionField('legSide', ca.legSide === 'destra'   ? null : 'destra');   break;
            case 'guard_closed':      setActionField('guard', ca.guard === 'chiusa' ? null : 'chiusa'); break;
            case 'guard_open':        setActionField('guard', ca.guard === 'aperta' ? null : 'aperta'); break;
            case 'distance_clinch':   setActionField('distance', ca.distance === 'clinch' ? null : 'clinch'); break;
            case 'tech_bonus':        setActionField('technicalBonus', !ca.technicalBonus); break;
            case 'sequence_toggle':   toggleSeqRef.current?.(); break;
            case 'preset_1': case 'preset_2': case 'preset_3': case 'preset_4': case 'preset_6': {
              // riusa la logica preset del Set 1
              const n = actionId.split('_')[1];
              const prev2 = activeShortcutRef.current;
              if (prev2) clearShortcut(prev2);
              if (prev2 === n) { activeShortcutRef.current = null; break; }
              activeShortcutRef.current = n;
              if (n === '1') { setActionField('technique','Pugno'); setActionField('target','corpo'); setActionField('outcome','punto'); setActionField('actionType','a'); }
              else if (n === '2') { setActionField('target','corpo'); setActionField('outcome','punto'); }
              else if (n === '3') { setActionField('target','testa'); setActionField('outcome','punto'); }
              else if (n === '4') { setActionField('target','corpo'); setActionField('technicalBonus',true); setActionField('outcome','punto'); }
              else if (n === '6') { setActionField('target','testa'); setActionField('technicalBonus',true); setActionField('outcome','punto'); }
              break;
            }
            case 'tech_Bn': {
              const on = ca.technique === 'Bn' && ca.legPosition !== 'anteriore';
              setActionField('technique',      on ? null : 'Bn');
              setActionField('target',         on ? null : 'corpo');
              setActionField('legPosition',    on ? null : 'posteriore');
              setActionField('actionType',     on ? null : 'a');
              break;
            }
            case 'tech_Bna': {
              const on = ca.technique === 'Bn' && ca.legPosition === 'anteriore';
              setActionField('technique',      on ? null : 'Bn');
              setActionField('target',         on ? null : 'corpo');
              setActionField('legPosition',    on ? null : 'anteriore');
              setActionField('actionType',     on ? null : 'a');
              break;
            }
            case 'tech_Pk': {
              // Pk — no actionType di default
              const on = ca.technique === 'Pk';
              setActionField('technique',      on ? null : 'Pk');
              setActionField('target',         on ? null : 'corpo');
              setActionField('legPosition',    on ? null : 'anteriore');
              break;
            }
            case 'tech_Dl': {
              const on = ca.technique === 'Dl' && ca.legPosition !== 'anteriore';
              setActionField('technique',      on ? null : 'Dl');
              setActionField('target',         on ? null : 'testa');
              setActionField('legPosition',    on ? null : 'posteriore');
              setActionField('actionType',     on ? null : 'a');
              break;
            }
            case 'tech_Dla': {
              const on = ca.technique === 'Dl' && ca.legPosition === 'anteriore';
              setActionField('technique',      on ? null : 'Dl');
              setActionField('target',         on ? null : 'testa');
              setActionField('legPosition',    on ? null : 'anteriore');
              setActionField('actionType',     on ? null : 'a');
              break;
            }
            case 'tech_Nr': {
              const on = ca.technique === 'Nr' && ca.legPosition !== 'anteriore';
              setActionField('technique',      on ? null : 'Nr');
              setActionField('target',         on ? null : 'testa');
              setActionField('legPosition',    on ? null : 'posteriore');
              setActionField('actionType',     on ? null : 'a');
              break;
            }
            case 'tech_Nra': {
              const on = ca.technique === 'Nr' && ca.legPosition === 'anteriore';
              setActionField('technique',      on ? null : 'Nr');
              setActionField('target',         on ? null : 'testa');
              setActionField('legPosition',    on ? null : 'anteriore');
              setActionField('actionType',     on ? null : 'a');
              break;
            }
            case 'tech_Dw': {
              const on = ca.technique === 'Dw';
              setActionField('technique',      on ? null  : 'Dw');
              setActionField('target',         on ? null  : 'corpo');
              setActionField('legPosition',    on ? null  : 'posteriore');
              setActionField('technicalBonus', on ? false : true);
              setActionField('actionType',     on ? null  : 'a');
              break;
            }
            case 'tech_Md': {
              const on = ca.technique === 'Md';
              setActionField('technique',      on ? null  : 'Md');
              setActionField('target',         on ? null  : 'testa');
              setActionField('legPosition',    on ? null  : 'posteriore');
              setActionField('technicalBonus', on ? false : true);
              setActionField('actionType',     on ? null  : 'a');
              break;
            }
            case 'tech_Pugno': {
              const on = ca.technique === 'Pugno';
              setActionField('technique',      on ? null : 'Pugno');
              setActionField('target',         on ? null : 'corpo');
              setActionField('legPosition',    on ? null : 'anteriore');
              setActionField('actionType',     on ? null : 'a');
              break;
            }
            case 'tech_2Bn': {
              const on = ca.technique === '2Bn';
              setActionField('technique',      on ? null : '2Bn');
              setActionField('target',         on ? null : 'corpo');
              setActionField('actionType',     on ? null : 'a');
              break;
            }
            case 'tech_2Bnf': {
              const on = ca.technique === '2Bn' && ca.technicalBonus;
              setActionField('technique',      on ? null  : '2Bn');
              setActionField('target',         on ? null  : 'corpo');
              setActionField('legPosition',    on ? null  : 'anteriore');
              setActionField('technicalBonus', on ? false : true);
              setActionField('actionType',     on ? null  : 'a');
              break;
            }
            case 'tech_360Bn': {
              const on = ca.technique === '360Bn';
              setActionField('technique',      on ? null  : '360Bn');
              setActionField('target',         on ? null  : 'corpo');
              setActionField('legPosition',    on ? null  : 'anteriore');
              setActionField('technicalBonus', on ? false : true);
              setActionField('actionType',     on ? null  : 'a');
              break;
            }
            case 'tech_360Dl': {
              const on = ca.technique === '360Dl';
              setActionField('technique',      on ? null  : '360Dl');
              setActionField('target',         on ? null  : 'testa');
              setActionField('legPosition',    on ? null  : 'anteriore');
              setActionField('technicalBonus', on ? false : true);
              setActionField('actionType',     on ? null  : 'a');
              break;
            }
            case 'tech_Gr': {
              const on = ca.technique === 'Gr';
              setActionField('technique',      on ? null : 'Gr');
              setActionField('target',         on ? null : 'corpo');
              setActionField('actionType',     on ? null : 'a');
              break;
            }
            case 'tech_Clsh': {
              // Clash — difesa, gamba anteriore (no actionType 'a')
              const on = ca.technique === 'Clsh';
              setActionField('technique',   on ? null : 'Clsh');
              setActionField('actionType',  on ? null : 'd');
              setActionField('legPosition', on ? null : 'anteriore');
              break;
            }
            case 'confirm':  handleConfirmRef.current?.(); break;
            case 'cancel':   onCancelRef.current?.(); break;
            default: break;
          }
        }
        return;
      }

      const k   = e.key.toLowerCase();
      const now = Date.now();

      // ── Sequenze a 2 tasti (finestra 600ms) ────────────────────────────
      const prev = prevKeyRef.current;
      if (now - prev.time < 600) {
        const seq = prev.key + k;
        if (seq === 'xs') {
          const cur = currentActionRef.current.legSide;
          setActionField('legSide', cur === 'sinistra' ? null : 'sinistra');
          prevKeyRef.current = { key: '', time: 0 };
          return;
        }
        if (seq === 'xd') {
          const cur = currentActionRef.current.legSide;
          setActionField('legSide', cur === 'destra' ? null : 'destra');
          prevKeyRef.current = { key: '', time: 0 };
          return;
        }
        if (seq === 'gc') {
          const cur = currentActionRef.current.guard;
          setActionField('guard', cur === 'chiusa' ? null : 'chiusa');
          prevKeyRef.current = { key: '', time: 0 };
          return;
        }
        if (seq === 'ga') {
          const cur = currentActionRef.current.guard;
          setActionField('guard', cur === 'aperta' ? null : 'aperta');
          prevKeyRef.current = { key: '', time: 0 };
          return;
        }
        if (seq === 'cl') {
          const cur = currentActionRef.current.distance;
          setActionField('distance', cur === 'clinch' ? null : 'clinch');
          prevKeyRef.current = { key: '', time: 0 };
          return;
        }
      }
      prevKeyRef.current = { key: k, time: now };

      // Atleta (toggle)
      if (k === 'b') { handleSelectAthleteRef.current?.('blue'); return; }
      if (k === 'r') { handleSelectAthleteRef.current?.('red');  return; }

      // Tipo azione (toggle)
      if (k === 'a') {
        const cur = currentActionRef.current.actionType;
        setActionField('actionType', cur === 'a' ? null : 'a');
        return;
      }
      if (k === 'd') {
        const cur = currentActionRef.current.actionType;
        setActionField('actionType', cur === 'd' ? null : 'd');
        return;
      }

      // Gruppo radio 1-6
      if (['1','2','3','4','6'].includes(e.key)) {
        const prev = activeShortcutRef.current;
        const next = e.key;

        // Spegni sempre il precedente (se c'era)
        if (prev) clearShortcut(prev);

        if (prev === next) {
          // Stesso tasto due volte → toggle off, niente da applicare
          activeShortcutRef.current = null;
        } else {
          // Applica il nuovo preset
          activeShortcutRef.current = next;
          switch (next) {
            case '1':
              handleTechniqueSelect('Pugno');
              setActionField('target',     'corpo');
              setActionField('outcome',    'punto');
              setActionField('actionType', 'a');
              break;
            case '2':
              setActionField('target',  'corpo');
              setActionField('outcome', 'punto');
              break;
            case '3':
              setActionField('target',  'testa');
              setActionField('outcome', 'punto');
              break;
            case '4':
              setActionField('target',         'corpo');
              setActionField('technicalBonus', true);
              setActionField('outcome',        'punto');
              break;
            case '6':
              setActionField('target',         'testa');
              setActionField('technicalBonus', true);
              setActionField('outcome',        'punto');
              break;
            default: break;
          }
        }
        return;
      }

      // Sequenza
      if (e.key === '+') { toggleSeqRef.current?.(); return; }

      // Conferma / Annulla
      if (k === 'enter')  { handleConfirmRef.current?.(); return; }
      if (k === 'escape') { onCancelRef.current?.(); setCustomName(''); return; }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // registrato una volta sola — legge lo stato via ref

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div ref={composerRef} className="ac-composer" tabIndex={-1}>

      {/* ── STEP 1: Atleta ─────────────────────────────────────────────── */}
      <section className="ac-step">
        <div className="ac-step-label">
          <span className="ac-step-num">1</span>
          Atleta
          <kbd>B</kbd><kbd>R</kbd>
        </div>
        <div className="ac-athlete-row">
          <button
            className={`ac-athlete-btn blue${currentAction.athlete === 'blue' ? ' selected' : ''}`}
            onClick={() => handleSelectAthlete('blue')}
          >
            <span className="ac-athlete-key">B</span>
            <span className="ac-athlete-color">BLU</span>
            <span className="ac-athlete-name">{blueName}</span>
          </button>
          <button
            className={`ac-athlete-btn red${currentAction.athlete === 'red' ? ' selected' : ''}`}
            onClick={() => handleSelectAthlete('red')}
          >
            <span className="ac-athlete-key">R</span>
            <span className="ac-athlete-color">ROSSO</span>
            <span className="ac-athlete-name">{redName}</span>
          </button>
        </div>
      </section>

      {/* ── STEP 2: Tipo azione ─────────────────────────────────────────── */}
      <section className="ac-step">
        <div className="ac-step-label">
          <span className="ac-step-num">2</span>
          Tipo azione
        </div>
        <div className="ac-action-type-row">
          {ACTION_TYPES.map(at => (
            <button
              key={at.id}
              className={`ac-type-btn${currentAction.actionType === at.id ? ' selected' : ''}`}
              onClick={() => setActionField('actionType', currentAction.actionType === at.id ? null : at.id)}
              title={at.label}
            >
              <span className="ac-type-abbr">{at.abbr}</span>
              <span className="ac-type-label">{at.label}</span>
              {at.key && <span className="ac-tech-key">{at.key}</span>}
            </button>
          ))}
        </div>
      </section>

      {/* ── STEP 3: Tecnica ─────────────────────────────────────────────── */}
      <section className="ac-step">
        <div className="ac-step-label">
          <span className="ac-step-num">3</span>
          Tecnica
        </div>
        <div className="ac-technique-grid">
          {ATC_TECHNIQUES.map((tech) => (
            <button
              key={tech.id}
              className={`ac-tech-btn${currentAction.technique === tech.id ? ' selected' : ''}`}
              onClick={() => handleTechniqueSelect(tech.id)}
              title={tech.name}
            >
              <span className="ac-tech-abbr">{tech.id}</span>
              <span className="ac-tech-name">{tech.name}</span>
              {tech.id === 'Pugno' && <span className="ac-tech-key">1</span>}
            </button>
          ))}
        </div>

        {currentAction.technique === 'custom' && (
          <input
            className="ac-custom-input"
            placeholder="Nome tecnica personalizzata…"
            value={customName}
            onChange={e => { setCustomName(e.target.value); setActionField('customTechnique', e.target.value); }}
          />
        )}

        {/* Biomeccanica collassabile */}
        <button
          className="ac-biomech-toggle"
          onClick={() => setShowBiomech(v => !v)}
        >
          {showBiomech ? '▲' : '▼'} Biomeccanica (opzionale)
        </button>

        {showBiomech && (
          <div className="ac-biomech">
            <BiomechRow
              label="Gamba"
              value={currentAction.legSide}
              options={[{ v: 'sinistra', l: 'Sx', hint: 'x→s' }, { v: 'destra', l: 'Dx', hint: 'x→d' }]}
              onChange={v => setActionField('legSide', v)}
            />
            <BiomechRow
              label="Posizione" value={currentAction.legPosition}
              options={[{ v: 'anteriore', l: 'Ant.' }, { v: 'posteriore', l: 'Post.' }]}
              onChange={v => setActionField('legPosition', v)}
            />
            <BiomechRow
              label="Guardia" value={currentAction.guard}
              options={[
                { v: 'aperta', l: 'Ga', hint: 'g→a' },
                { v: 'chiusa', l: 'Gc', hint: 'g→c' },
              ]}
              onChange={v => setActionField('guard', v)}
            />
            <BiomechRow
              label="Distanza" value={currentAction.distance}
              options={[
                { v: 'corta',   l: 'Corta'  },
                { v: 'media',   l: 'Media'  },
                { v: 'lunga',   l: 'Lunga'  },
                { v: 'clinch',  l: 'Clinch', hint: 'c→l' },
              ]}
              onChange={v => setActionField('distance', v)}
            />
          </div>
        )}
      </section>

      {/* ── STEP 4: Esito ───────────────────────────────────────────────── */}
      <section className="ac-step ac-step-outcome">
        <div className="ac-step-label">
          <span className="ac-step-num">4</span>
          Esito
        </div>

        <div className="ac-outcome-row">
          {/* Bersaglio */}
          {!isClash && (
            <div className="ac-outcome-col">
              <span className="ac-outcome-sublabel">Bersaglio</span>

              {/* Corpo */}
              <button
                className={`ac-outcome-btn${currentAction.target === 'corpo' ? ' selected' : ''}`}
                onClick={() => setActionField('target', currentAction.target === 'corpo' ? null : 'corpo')}
              >
                <span>Corpo</span>
                <span className="ac-outcome-keys"><kbd>2</kbd><kbd>4</kbd>+rot</span>
              </button>

              {/* Testa */}
              <button
                className={`ac-outcome-btn${currentAction.target === 'testa' ? ' selected' : ''}`}
                onClick={() => setActionField('target', currentAction.target === 'testa' ? null : 'testa')}
              >
                <span>Testa</span>
                <span className="ac-outcome-keys"><kbd>3</kbd><kbd>6</kbd>+rot</span>
              </button>
            </div>
          )}

          {/* Esito */}
          <div className="ac-outcome-col">
            <span className="ac-outcome-sublabel">Esito</span>
            {isClash && <div className="ac-clash-note">Parata — no punti</div>}
            {[
              { v: 'punto', l: 'Punto'   },
              { v: 'tocco', l: 'Tocco ~' },
              { v: 'miss',  l: 'Miss ✕'  },
            ].map(opt => (
              <button
                key={opt.v}
                className={`ac-outcome-btn${currentAction.outcome === opt.v ? ' selected' : ''}${isClash && opt.v === 'punto' ? ' disabled' : ''}`}
                disabled={isClash && opt.v === 'punto'}
                onClick={() => !(isClash && opt.v === 'punto') && setActionField('outcome', currentAction.outcome === opt.v ? null : opt.v)}
              >
                {opt.l}
              </button>
            ))}
          </div>

          {/* Punti + bonus rotazione */}
          <div className="ac-outcome-col ac-outcome-col-pts">
            <div className={`ac-pts-number${pts === 0 ? ' zero' : ''}`}>{pts}</div>
            <div className="ac-pts-label">{pts === 1 ? 'punto' : 'punti'}</div>
            <label className="ac-bonus-row">
              <input
                type="checkbox"
                checked={!!currentAction.technicalBonus}
                onChange={e => setActionField('technicalBonus', e.target.checked)}
              />
              <span>Rot. +1</span>
            </label>
          </div>
        </div>

        {/* Messaggi di validazione */}
        {!isValid && (
          <div className="ac-validation">
            {!currentAction.athlete    && <span>• atleta</span>}
            {!currentAction.actionType && <span>• tipo</span>}
            {!currentAction.technique  && <span>• tecnica</span>}
            {!currentAction.outcome    && <span>• esito</span>}
            {!isClash && currentAction.outcome === 'punto' && !currentAction.target && <span>• bersaglio</span>}
          </div>
        )}

        {/* Bottoni azione */}
        <div className="ac-action-btns">
          {isEditing && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 6, width: '100%' }}>
              <span style={{ fontSize: 10, color: '#FF9800', fontWeight: 700, alignSelf: 'center', marginRight: 4 }}>
                ✏ MODIFICA
              </span>
              <button
                className="ac-cancel-btn"
                style={{ flex: 1 }}
                onClick={onMarkStart}
                title="Segna timestamp inizio azione al momento corrente del video"
              >
                ▶ Inizio{currentAction.timestamp_start != null ? ` ${currentAction.timestamp_start}s` : ''}
              </button>
              <button
                className="ac-cancel-btn"
                style={{ flex: 1 }}
                onClick={onMarkEnd}
                title="Segna timestamp fine azione (opzionale)"
              >
                ■ Fine{currentAction.timestamp_end != null ? ` ${currentAction.timestamp_end}s` : ''}
              </button>
            </div>
          )}
          <button className="ac-confirm-btn" disabled={!isValid} onClick={handleConfirm}>
            {isEditing ? '✓ Aggiorna' : '✓ Conferma'} <kbd>Enter</kbd>
          </button>
          <button className="ac-cancel-btn" onClick={() => { onCancel(); setCustomName(''); }}>
            Annulla <kbd>Esc</kbd>
          </button>
          <button
            className={`ac-seq-btn${seqActive ? ' active' : ''}`}
            onClick={toggleSeq}
            title={seqActive ? 'Sequenza attiva — premi + per disattivare' : 'Attiva modalità sequenza (+)'}
          >
            ⛓ Seq <kbd>+</kbd>
          </button>
        </div>
      </section>

      {/* ── Penalità ─────────────────────────────────────────────────────── */}
      <section className="ac-step ac-penalties">
        <div className="ac-step-label" style={{ marginBottom: 6 }}>
          <span className="ac-step-num" style={{ background: 'var(--color-orange)' }}>GJ</span>
          Gam-jeom
        </div>
        <select
          className="ac-penalty-select"
          value={penaltyType}
          onChange={e => setPenaltyType(e.target.value)}
        >
          {PENALTY_TYPES.map(pt => <option key={pt} value={pt}>{pt}</option>)}
        </select>
        <div className="ac-penalty-row">
          <span className="ac-penalty-athlete blue">{blueName}</span>
          <button className="ac-pen-btn" onClick={() => {
            if (bluePens.length === 0) return;
            onDeletePenalty(bluePens[bluePens.length - 1].id);
          }}>−</button>
          <span className="ac-pen-count blue">{bluePens.length}</span>
          <button className="ac-pen-btn ac-pen-btn-add blue" onClick={() => onAddPenalty({ athlete: 'blue', penalty_type: penaltyType })}>+</button>
        </div>
        <div className="ac-penalty-row">
          <span className="ac-penalty-athlete red">{redName}</span>
          <button className="ac-pen-btn" onClick={() => {
            if (redPens.length === 0) return;
            onDeletePenalty(redPens[redPens.length - 1].id);
          }}>−</button>
          <span className="ac-pen-count red">{redPens.length}</span>
          <button className="ac-pen-btn ac-pen-btn-add red" onClick={() => onAddPenalty({ athlete: 'red', penalty_type: penaltyType })}>+</button>
        </div>
      </section>

    </div>
  );
}

// ── Helper: riga biomeccanica ─────────────────────────────────────────────────

function BiomechRow({ label, value, options, onChange }) {
  return (
    <div className="ac-biomech-row">
      <span className="ac-biomech-label">{label}</span>
      <div className="ac-biomech-opts">
        {options.map(opt => (
          <button
            key={opt.v}
            className={`ac-biomech-btn${value === opt.v ? ' selected' : ''}`}
            onClick={() => onChange(value === opt.v ? null : opt.v)}
          >
            {opt.l}
            {opt.hint && <kbd style={{ marginLeft: 4 }}>{opt.hint}</kbd>}
          </button>
        ))}
      </div>
    </div>
  );
}

export default ActionComposer;
